import { CheckCircle, Trash, UploadSimple } from '@phosphor-icons/react';
import type { PersonaDto, ReferenceShot } from '@reelplicate/api-client/model';
import {
  getGetPersonaQueryKey,
  getListPersonasQueryKey,
  useCreatePersonaReferenceUpload,
  useRemovePersonaReference,
  useSetPersonaReference,
} from '@reelplicate/api-client/personas';
import { Badge, IconButton, Inline, Stack, toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import {
  compressPhoto,
  photoProblem,
  type PhotoType,
  putWithProgress,
} from '@/lib/upload';

import { SHOT_GUIDES } from './shot-guides';
import { SHOTS } from './shots';

/**
 * A persona's reference look (Figma 435:3338): five photos from set angles.
 * When a flow generates images of the persona, these go along so the face,
 * hair and body stay the same. Each tile uploads straight to storage, then
 * becomes that shot; replacing one deletes the old photo.
 */
export function ReferenceLook({
  workspaceId,
  persona,
  canEdit,
  uploadsEnabled,
}: {
  workspaceId: string;
  persona: PersonaDto;
  canEdit: boolean;
  uploadsEnabled: boolean;
}) {
  const queryClient = useQueryClient();
  const createUpload = useCreatePersonaReferenceUpload();
  const setReference = useSetPersonaReference();
  const removeReference = useRemovePersonaReference();
  const [progress, setProgress] = useState<Partial<Record<ReferenceShot, number>>>({});
  const input = useRef<HTMLInputElement>(null);
  const [picking, setPicking] = useState<ReferenceShot | null>(null);

  const saved = (next: PersonaDto) => {
    queryClient.setQueryData(getGetPersonaQueryKey(workspaceId, persona.id), next);
    void queryClient.invalidateQueries({
      queryKey: getListPersonasQueryKey(workspaceId),
    });
  };

  const upload = async (shot: ReferenceShot, picked: File) => {
    const problem = await photoProblem(picked);
    if (problem) {
      toast.error(problem);
      return;
    }
    setProgress((p) => ({ ...p, [shot]: 0 }));
    try {
      const file = await compressPhoto(picked);
      const { url, key } = await createUpload.mutateAsync({
        id: workspaceId,
        personaId: persona.id,
        shot,
        data: { contentType: file.type as PhotoType, size: file.size },
      });
      await putWithProgress(url, file, {
        onProgress: (percent) => setProgress((p) => ({ ...p, [shot]: percent })),
      });
      saved(
        await setReference.mutateAsync({
          id: workspaceId,
          personaId: persona.id,
          shot,
          data: { key },
        }),
      );
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('Upload failed')) {
        toast.error('The photo didn’t upload. Try again.');
      }
      // API failures are already shown as a toast.
    } finally {
      setProgress((p) => {
        const next = { ...p };
        delete next[shot];
        return next;
      });
    }
  };

  const remove = async (shot: ReferenceShot) => {
    try {
      saved(
        await removeReference.mutateAsync({
          id: workspaceId,
          personaId: persona.id,
          shot,
        }),
      );
    } catch {
      // Shown as a toast.
    }
  };

  const count = persona.referenceCount;
  const who = persona.shortName ?? persona.name;
  const missing = SHOTS.filter((s) => !persona.reference.some((r) => r.shot === s.shot));
  const photo = (shot: ReferenceShot) => persona.reference.find((r) => r.shot === shot);

  return (
    <Stack gap={5}>
      <Inline gap={2} alignY="center" wrap>
        <Badge
          tone={count === SHOTS.length ? 'success' : count ? 'warning' : 'neutral'}
          size="sm"
        >
          {count} of {SHOTS.length} shots
        </Badge>
        <p className="text-body-sm text-text-secondary">
          {count === SHOTS.length
            ? `Every angle is covered - generated images keep ${who} looking the same.`
            : `Add ${missing
                .map((s) => s.title.toLowerCase())
                .join(', ')
                .replace(
                  /, ([^,]*)$/,
                  ' and $1',
                )} so every kind of scene looks like ${who}.`}
        </p>
      </Inline>

      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        tabIndex={-1}
        aria-label="Reference photo file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file && picking) void upload(picking, file);
        }}
      />

      <ul aria-label="Reference shots" className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {SHOTS.map(({ shot, title, hint }) => {
          const current = photo(shot);
          const percent = progress[shot];
          const choose = () => {
            setPicking(shot);
            input.current?.click();
          };
          return (
            <li key={shot} className="flex flex-col gap-2">
              <div
                className={`relative aspect-[3/4] overflow-hidden rounded-md ${
                  current
                    ? 'bg-bg-muted'
                    : 'bg-bg-subtle border-border-default border border-dashed'
                }`}
              >
                {current ? (
                  <img
                    src={current.url}
                    alt={`${title}: ${persona.name}`}
                    className="size-full object-cover"
                  />
                ) : (
                  <svg
                    viewBox="0 0 158 211"
                    preserveAspectRatio="xMidYMin meet"
                    aria-hidden
                    data-guide={shot}
                    className="text-border-strong size-full opacity-60"
                  >
                    {SHOT_GUIDES[shot].map((d) => (
                      <path
                        key={d}
                        d={d}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                      />
                    ))}
                  </svg>
                )}
                {percent !== undefined && (
                  <div className="bg-bg-overlay text-text-inverse text-label-sm absolute inset-0 flex items-center justify-center">
                    Uploading {percent}%
                  </div>
                )}
                {canEdit && percent === undefined && (
                  <>
                    {current ? (
                      <Inline gap={1} className="absolute top-1.5 right-1.5">
                        <IconButton
                          type="button"
                          icon={UploadSimple}
                          size="sm"
                          variant="secondary"
                          aria-label={`Replace ${title.toLowerCase()} photo`}
                          disabled={!uploadsEnabled}
                          onClick={choose}
                        />
                        <IconButton
                          type="button"
                          icon={Trash}
                          size="sm"
                          variant="secondary"
                          aria-label={`Remove ${title.toLowerCase()} photo`}
                          onClick={() => void remove(shot)}
                        />
                      </Inline>
                    ) : (
                      <button
                        type="button"
                        disabled={!uploadsEnabled}
                        onClick={choose}
                        aria-label={`Upload ${title.toLowerCase()} photo`}
                        className="bg-bg-surface text-label-sm text-text-primary focus-visible:ring-border-focus absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-md px-2.5 py-1 shadow-sm outline-none focus-visible:ring-2 disabled:opacity-50"
                      >
                        <UploadSimple aria-hidden className="size-3.5" />
                        Upload
                      </button>
                    )}
                  </>
                )}
              </div>
              <Stack gap={0.5}>
                <Inline gap={1.5} alignY="center">
                  {current && (
                    <CheckCircle
                      aria-hidden
                      weight="fill"
                      className="text-status-success-solid size-3.5"
                    />
                  )}
                  <p className="text-label-md text-text-primary">{title}</p>
                </Inline>
                <p className="text-caption text-text-tertiary">{hint}</p>
              </Stack>
            </li>
          );
        })}
      </ul>

      <p className="text-caption text-text-tertiary">
        {uploadsEnabled
          ? 'For the most consistent results: the same person in every shot, a plain background, even light, no sunglasses or hats, and nobody else in the photo. JPG, PNG or WebP, up to 20 MB each.'
          : 'Uploads aren’t set up yet.'}
      </p>
    </Stack>
  );
}
